import { useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api from "@/lib/api";
import { Megaphone, X, UserPlus, Flag, Trophy, Share2, Medal } from "lucide-react";

const SEEN_KEY = "tt_seen_announcements";

const CARTOON = {
  rc: "https://static.prod-images.emergentagent.com/jobs/80d61861-c1ca-4734-bc3d-d13d8faf2a1b/images/aec6a8dae258a0b0516e76000aaf84da9290f29ba47f72f2a165528386de565b.jpeg",
  sim: "https://static.prod-images.emergentagent.com/jobs/80d61861-c1ca-4734-bc3d-d13d8faf2a1b/images/739d1dcc5cf8ba1f6ddf77fde5e74a8bb59b2e7fd59456b44a143b5a85f995b6.jpeg",
};
const DEFAULT_CARTOON = "https://static.prod-images.emergentagent.com/jobs/80d61861-c1ca-4734-bc3d-d13d8faf2a1b/images/edb6ee79ad019ec08facb38221e55058fc8bb48f918a2987df704147b3eb3aac.jpeg";

const getSeen = () => {
  try { return JSON.parse(localStorage.getItem(SEEN_KEY) || "[]"); }
  catch { return []; }
};

const medalColor = (p) => (p === 1 ? "text-yellow-400" : p === 2 ? "text-zinc-300" : "text-amber-600");

export default function AnnouncementPopup() {
  const [current, setCurrent] = useState(null);
  const navigate = useNavigate();
  const didRun = useRef(false);

  useEffect(() => {
    if (didRun.current) return;
    didRun.current = true;
    api.get("/announcements").then((r) => {
      const seen = getSeen();
      const next = (r.data || []).find((a) => !seen.includes(a.id));
      if (next) setCurrent(next);
    }).catch(() => {});
  }, []);

  const dismiss = () => {
    if (current) {
      const seen = getSeen();
      if (!seen.includes(current.id)) localStorage.setItem(SEEN_KEY, JSON.stringify([...seen, current.id]));
    }
    setCurrent(null);
  };

  const goEnroll = () => {
    const path = current?.link_path;
    dismiss();
    if (path) navigate(path);
  };

  const sharePodium = async () => {
    const top = (current?.podium || []).slice(0, 3);
    if (!top.length) return;
    const accent = current.category === "rc" ? "#facc15" : "#06b6d4";
    const W = 1080, H = 1080;
    const c = document.createElement("canvas");
    c.width = W; c.height = H;
    const ctx = c.getContext("2d");
    ctx.fillStyle = "#09090b"; ctx.fillRect(0, 0, W, H);
    ctx.fillStyle = accent; ctx.fillRect(0, 0, W, 10);
    const logo = new Image();
    logo.src = "/team-tuono-logo.png";
    await new Promise((res) => { logo.onload = res; logo.onerror = res; });
    if (logo.complete && logo.naturalWidth) { try { ctx.drawImage(logo, W - 168, 44, 120, 120); } catch (e) { /* skip */ } }
    ctx.textAlign = "left";
    ctx.fillStyle = accent; ctx.font = "bold 28px Arial"; ctx.fillText("TEAM TUONO", 56, 92);
    ctx.fillStyle = "#fafafa"; ctx.font = "900 62px Arial"; ctx.fillText((current.title || "").toUpperCase().slice(0, 20), 56, 172);
    ctx.fillStyle = "#a1a1aa"; ctx.font = "26px Arial";
    ctx.fillText(("PODIO" + (current.track ? " · " + current.track : "")).slice(0, 46), 56, 224);
    const base = 950;
    const bars = [{ pos: 2, cx: 272, w: 236, h: 300 }, { pos: 1, cx: 540, w: 260, h: 410 }, { pos: 3, cx: 808, w: 236, h: 236 }];
    const color = (p) => (p === 1 ? "#facc15" : p === 2 ? "#d4d4d8" : "#d97706");
    const byPos = {};
    top.forEach((r) => { byPos[r.position] = r; });
    bars.forEach((b) => {
      const r = byPos[b.pos];
      if (!r) return;
      const x = b.cx - b.w / 2, y = base - b.h;
      ctx.fillStyle = "#141417"; ctx.fillRect(x, y, b.w, b.h);
      ctx.fillStyle = color(b.pos); ctx.fillRect(x, y, b.w, 8);
      ctx.textAlign = "center";
      ctx.fillStyle = color(b.pos); ctx.font = "900 96px Arial"; ctx.fillText(String(b.pos), b.cx, base - b.h / 2 + 24);
      ctx.fillStyle = "#fafafa"; ctx.font = "bold 32px Arial"; ctx.fillText((r.name || "").slice(0, 16), b.cx, y - 48);
      ctx.fillStyle = accent; ctx.font = "bold 26px Arial"; ctx.fillText(r.points ? r.points + " pt" : (r.time || ""), b.cx, y - 14);
    });
    ctx.textAlign = "left";
    const blob = await new Promise((res) => c.toBlob(res, "image/png"));
    const file = new File([blob], "podio.png", { type: "image/png" });
    try {
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: current.title, text: `Podio ${current.title} — Team Tuono` });
        return;
      }
    } catch (e) { /* fallback */ }
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `podio-${current.title}.png`; a.click();
    URL.revokeObjectURL(url);
    toast.success("Locandina podio generata");
  };

  const isPodium = current?.type === "podium";
  const accent = current?.category === "rc" ? "text-yellow-400" : "text-cyan-400";

  return (
    <AnimatePresence>
      {current && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] flex items-start sm:items-center justify-center p-4 bg-zinc-950/85 backdrop-blur-sm overflow-y-auto"
          data-testid="announcement-popup">
          <motion.div
            initial={{ scale: 0.94, y: 16 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.94, y: 16 }}
            transition={{ type: "spring", stiffness: 260, damping: 24 }}
            className="relative w-full max-w-md my-8 max-h-[90vh] overflow-y-auto border border-zinc-700 bg-zinc-900 shadow-2xl">
            <button onClick={dismiss} data-testid="announcement-close"
              className="absolute top-3 right-3 z-10 h-9 w-9 flex items-center justify-center rounded-full bg-zinc-950/70 text-zinc-300 hover:text-white hover:bg-zinc-800 transition-colors duration-200">
              <X className="h-5 w-5" />
            </button>

            {isPodium ? (
              <div data-testid="podium-popup">
                <div className="relative h-28 bg-gradient-to-br from-yellow-500/20 via-zinc-900 to-zinc-950 flex items-end px-6 pb-3">
                  <div className="flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-yellow-300">
                    <Trophy className="h-4 w-4" /> Podio Gara · Terminata
                  </div>
                </div>
                <div className="p-6">
                  <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-zinc-50">{current.title}</h2>
                  {current.subtitle && <p className={`mt-1 text-sm font-bold ${accent}`}>{current.subtitle}</p>}
                  {current.track && <p className="mt-1 flex items-center gap-2 text-sm text-zinc-400"><Flag className="h-3.5 w-3.5" /> {current.track}</p>}

                  <div className="mt-5 space-y-2">
                    {(current.podium || []).map((p) => (
                      <div key={p.position} data-testid={`podium-row-${p.position}`}
                        className={`flex items-center gap-3 border px-4 py-3 rounded-md ${p.position === 1 ? "border-yellow-400/50 bg-yellow-400/10" : "border-zinc-700 bg-zinc-900/70"}`}>
                        <Medal className={`h-5 w-5 ${medalColor(p.position)}`} />
                        <span className="font-display font-bold text-zinc-400 w-4">{p.position}</span>
                        <span className="flex-1 font-heading font-bold text-zinc-50 truncate">{p.name}</span>
                        <span className="font-display font-bold text-yellow-400">{p.points ? `${p.points} pt` : p.time}</span>
                      </div>
                    ))}
                  </div>

                  <div className="mt-6 flex gap-3">
                    <button onClick={sharePodium} data-testid="podium-share-btn"
                      className="flex-1 inline-flex items-center justify-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3.5 hover:bg-yellow-300 transition-colors duration-200">
                      <Share2 className="h-4 w-4" /> Condividi podio
                    </button>
                    <button onClick={goEnroll} data-testid="podium-goto-btn"
                      className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-300 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3.5 hover:border-zinc-500 hover:text-zinc-100 transition-colors duration-200">
                      Vai alla gara
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <div className="relative h-40 overflow-hidden">
                  <img src={current.image_url || CARTOON[current.category] || DEFAULT_CARTOON} alt="" className="absolute inset-0 w-full h-full object-cover opacity-60" />
                  <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/50 to-transparent" />
                  <div className="absolute bottom-3 left-5 flex items-center gap-2 text-[10px] font-bold tracking-[0.25em] uppercase text-zinc-100">
                    <Megaphone className={`h-4 w-4 ${accent}`} />
                    {current.type === "championship" ? "Nuovo Campionato" : current.type === "event" ? "Nuovo Gran Premio" : "Locandina"}
                  </div>
                </div>
                <div className="p-6">
                  <h2 className="font-heading text-2xl sm:text-3xl font-black uppercase tracking-tight text-zinc-50" data-testid="announcement-title">
                    {current.title}
                  </h2>
                  {current.subtitle && <p className={`mt-1 text-sm font-bold ${accent}`}>{current.subtitle}</p>}
                  {current.meta && (
                    <p className="mt-2 flex items-center gap-2 text-sm text-zinc-400"><Flag className="h-3.5 w-3.5 shrink-0" /> {current.meta}</p>
                  )}
                  {current.cost > 0 && (
                    <p className="mt-3 inline-flex items-center border border-yellow-400/40 bg-yellow-400/10 text-yellow-400 text-xs font-bold px-3 py-1.5 rounded-full">
                      Quota €{current.cost}
                    </p>
                  )}
                  <div className="mt-6 flex gap-3">
                    {current.type === "custom" && !current.link_path ? (
                      <button onClick={dismiss} data-testid="announcement-close-btn"
                        className="flex-1 inline-flex items-center justify-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3.5 hover:bg-yellow-300 transition-colors duration-200">
                        Chiudi
                      </button>
                    ) : (
                      <>
                        <button onClick={goEnroll} data-testid="announcement-enroll-btn"
                          className="flex-1 inline-flex items-center justify-center gap-2 bg-yellow-400 text-zinc-950 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3.5 hover:bg-yellow-300 transition-colors duration-200">
                          <UserPlus className="h-4 w-4" /> {current.type === "custom" ? "Scopri" : "Iscriviti"}
                        </button>
                        <button onClick={dismiss} data-testid="announcement-dismiss-btn"
                          className="inline-flex items-center justify-center gap-2 border border-zinc-700 text-zinc-300 font-bold text-xs tracking-[0.15em] uppercase px-5 py-3.5 hover:border-zinc-500 hover:text-zinc-100 transition-colors duration-200">
                          Più tardi
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
